import { type ChipProps, Stack, Typography } from '@mui/material'
import React, { useMemo } from 'react'

import { ChipWithTooltip } from 'components/common/chips/ChipWithTooltip'

type TagValue = string | string[]

interface ChipListProps<TValue extends TagValue>
  extends Pick<ChipProps, 'color' | 'size' | 'sx' | 'variant'> {
  tags: Record<string, TValue>
  visibleTagsCount?: number
  onDeleteTag?([k, v]: [string, TValue]): void
}

export type ChipListSingleValueProps = ChipListProps<string>

export type ChipListMultiValueProps = ChipListProps<string[]>

function ChipListBase<TValue extends TagValue>({
  onDeleteTag,
  tags,
  visibleTagsCount,
  ...chipProps
}: ChipListProps<TValue>) {
  const tagData = useMemo(() => {
    const allTags = Object.entries(tags).map(([key, value]) => {
      const valueString = Array.isArray(value) ? value.join(', ') : value
      const label = `${key}: ${valueString}`

      return { key, label, value }
    })

    return visibleTagsCount
      ? {
          visible: allTags.slice(0, visibleTagsCount),
          hidden: allTags.slice(visibleTagsCount)
        }
      : { visible: allTags, hidden: [] }
  }, [tags, visibleTagsCount])

  const hiddenLabel =
    tagData.hidden.length > 0 ? `+${tagData.hidden.length}` : undefined

  return (
    <>
      {tagData.visible.map(({ key, label, value }) => {
        return (
          <ChipWithTooltip
            key={key}
            label={label}
            tooltipProps={{ title: label }}
            {...chipProps}
            {...(onDeleteTag && { onDelete: () => onDeleteTag([key, value]) })}
          />
        )
      })}
      {hiddenLabel && (
        <ChipWithTooltip
          variant="outlined"
          {...chipProps}
          aria-label={hiddenLabel}
          label={hiddenLabel}
          tooltipProps={{
            title: (
              <Stack sx={{ gap: 0.5 }}>
                {tagData.hidden.map(({ key, label }) => {
                  return (
                    <Typography key={key} variant="body2">
                      {label}
                    </Typography>
                  )
                })}
              </Stack>
            )
          }}
        />
      )}
    </>
  )
}

export const ChipListSingleValue = ChipListBase<string>

export const ChipListMultiValue = ChipListBase<string[]>
